/** Result of parsing JSON text with a position for the error. */
export type NxJsonParseResult =
  | { ok: true; value: unknown }
  | { ok: false; message: string; line?: number; column?: number };

/** 1-based line and column of a character offset in `text`. */
function lineColumn(text: string, offset: number): { line: number; column: number } {
  const before = text.slice(0, Math.max(0, Math.min(offset, text.length)));
  const lines = before.split('\n');
  return { line: lines.length, column: lines[lines.length - 1]!.length + 1 };
}

/**
 * Offset of the first character that breaks the JSON grammar, or `null` when
 * the text is valid. A small validating scanner (it builds no value): the
 * engines' messages often name no position (current V8: „Unexpected token
 * '}'“), and the end of the text is the wrong guess for most mistakes.
 */
export function locateJsonError(text: string): number | null {
  let i = 0;
  const n = text.length;
  const ws = () => {
    while (i < n && ' \t\n\r'.includes(text[i]!)) i++;
  };
  const fail = (): never => {
    throw i;
  };
  const literal = (word: string) => {
    for (const ch of word) {
      if (text[i] !== ch) fail();
      i++;
    }
  };
  const string = () => {
    i++; // opening quote
    while (i < n) {
      const ch = text[i]!;
      if (ch === '"') {
        i++;
        return;
      }
      if (ch === '\\') {
        i++;
        const esc = text[i];
        if (esc === 'u') {
          for (let k = 1; k <= 4; k++) {
            if (!/[0-9a-fA-F]/.test(text[i + k] ?? '')) {
              i += k;
              fail();
            }
          }
          i += 5;
          continue;
        }
        if (esc === undefined || !'"\\/bfnrt'.includes(esc)) fail();
        i++;
        continue;
      }
      if (ch < ' ') fail();
      i++;
    }
    fail();
  };
  const number = () => {
    if (text[i] === '-') i++;
    if (text[i] === '0') i++;
    else if (/[1-9]/.test(text[i] ?? '')) while (/[0-9]/.test(text[i] ?? '')) i++;
    else fail();
    if (text[i] === '.') {
      i++;
      if (!/[0-9]/.test(text[i] ?? '')) fail();
      while (/[0-9]/.test(text[i] ?? '')) i++;
    }
    if (text[i] === 'e' || text[i] === 'E') {
      i++;
      if (text[i] === '+' || text[i] === '-') i++;
      if (!/[0-9]/.test(text[i] ?? '')) fail();
      while (/[0-9]/.test(text[i] ?? '')) i++;
    }
  };
  const value = (): void => {
    ws();
    const ch = text[i];
    if (ch === '{') {
      i++;
      ws();
      if (text[i] === '}') {
        i++;
        return;
      }
      for (;;) {
        ws();
        if (text[i] !== '"') fail();
        string();
        ws();
        if (text[i] !== ':') fail();
        i++;
        value();
        ws();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === '}') {
          i++;
          return;
        }
        fail();
      }
    }
    if (ch === '[') {
      i++;
      ws();
      if (text[i] === ']') {
        i++;
        return;
      }
      for (;;) {
        value();
        ws();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === ']') {
          i++;
          return;
        }
        fail();
      }
    }
    if (ch === '"') return string();
    if (ch === 't') return literal('true');
    if (ch === 'f') return literal('false');
    if (ch === 'n') return literal('null');
    return number();
  };
  try {
    value();
    ws();
    if (i < n) fail();
    return null;
  } catch (at) {
    return typeof at === 'number' ? Math.min(at, n) : null;
  }
}

/**
 * Parses JSON text. On failure the engine's message is kept; the error
 * position comes from the message where it names one („position N“, „line L
 * column C“), else from `locateJsonError`.
 */
export function parseJson(text: string): NxJsonParseResult {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const lc = /line (\d+) column (\d+)/i.exec(message);
    if (lc) return { ok: false, message, line: Number(lc[1]), column: Number(lc[2]) };
    const pos = /position (\d+)/i.exec(message);
    const at = pos ? Number(pos[1]) : locateJsonError(text);
    if (at === null) return { ok: false, message };
    return { ok: false, message, ...lineColumn(text, at) };
  }
}

/** Pretty-prints `value` with two spaces, or returns `null` for unserialisable values. */
export function stringifyJson(value: unknown): string | null {
  try {
    const out = JSON.stringify(value, null, 2);
    return out === undefined ? null : out;
  } catch {
    return null;
  }
}
